import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Paper,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import ReminderMessageConfig from './ReminderMessageConfig';
import { createDefaultMessage } from './reminderCampaignDefaults';

const ReminderMessageList = ({
  messages,
  onMessagesChange,
  templates,
  communicationEndpoints,
  channel,
  timeIndex,
}) => {
  // State for managing expanded voice configurations
  const [expandedVoiceConfigs, setExpandedVoiceConfigs] = useState({});

  const handleMessageChange = (index, updatedMessage) => {
    const newMessages = [...messages];
    newMessages[index] = updatedMessage;
    onMessagesChange(newMessages);
  };

  const handleAddMessage = () => {
    const newMessage = createDefaultMessage(channel);
    onMessagesChange([...messages, newMessage]);
  };

  const handleDeleteMessage = (index) => {
    const newMessages = messages.filter((_, i) => i !== index);
    onMessagesChange(newMessages);
    
    // Clean up expanded state for deleted message
    const newExpandedState = { ...expandedVoiceConfigs };
    delete newExpandedState[index];
    setExpandedVoiceConfigs(newExpandedState);
  };

  const handleToggleVoiceConfig = (messageIndex) => {
    setExpandedVoiceConfigs(prev => ({
      ...prev,
      [messageIndex]: !prev[messageIndex]
    }));
  };

  const handleExpandAllVoiceConfigs = (messageIndex) => {
    setExpandedVoiceConfigs(prev => ({
      ...prev,
      [messageIndex]: true
    }));
  };

  const handleCollapseAllVoiceConfigs = (messageIndex) => {
    setExpandedVoiceConfigs(prev => ({
      ...prev,
      [messageIndex]: false
    }));
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">
          Messages
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddMessage}
          size="small"
        >
          Add Message
        </Button>
      </Box>

      {messages.map((message, index) => (
        <Paper
          key={index}
          sx={{
            mb: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <ReminderMessageConfig
            message={message}
            onMessageChange={(updatedMessage) => handleMessageChange(index, updatedMessage)}
            onDelete={() => handleDeleteMessage(index)}
            templates={templates}
            communicationEndpoints={communicationEndpoints}
            channel={channel}
            index={index}
            expandedVoiceConfigs={expandedVoiceConfigs}
            onToggleVoiceConfig={() => handleToggleVoiceConfig(index)}
            onExpandAllVoiceConfigs={() => handleExpandAllVoiceConfigs(index)}
            onCollapseAllVoiceConfigs={() => handleCollapseAllVoiceConfigs(index)}
          />
        </Paper>
      ))}

      {messages.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No messages configured. Click "Add Message" to add a new message.
        </Typography>
      )}
    </Box>
  );
};

ReminderMessageList.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      template: PropTypes.string,
      email_config: PropTypes.object,
      sms_config: PropTypes.object,
      voice_config: PropTypes.object,
      chat_config: PropTypes.object,
    })
  ).isRequired,
  onMessagesChange: PropTypes.func.isRequired,
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  communicationEndpoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  channel: PropTypes.oneOf(['email', 'sms', 'voice', 'chat']).isRequired,
  timeIndex: PropTypes.number.isRequired,
};

export default ReminderMessageList; 